import * as z from "zod/mini";

/**
 * Room session tokens (ADR 0010, P2-API-03). The api signs them with Ed25519, and realtime verifies
 * them with the api's public keys before a socket reaches the room.
 *
 * - **Shape:** `<payload>.<signature>`, both base64url. The payload is the JSON claims below; the
 *   signature is Ed25519 over the payload's characters.
 * - **On the wire:** the client offers two WebSocket subprotocols, `ROOM_PROTOCOL` and
 *   `TOKEN_PREFIX + token`, so the token never appears in a URL or a log. The server answers with
 *   `ROOM_PROTOCOL`.
 */
export const ROOM_PROTOCOL = "obean.v1";
export const TOKEN_PREFIX = "token.";
/** Seconds a token is good for: long enough to connect, too short to be worth stealing. */
export const ROOM_TOKEN_TTL_S = 120;

const id = z.string().check(z.regex(/^[A-Za-z0-9_-]{1,64}$/));
/** A bean extra's id (ADR 0018). The format only: a client draws an id it doesn't know as plain. */
export const BEAN_EXTRA = /^[a-z][a-z0-9-]{0,31}$/;

export const roomTokenClaims = z.object({
  v: z.literal(1),
  /** The guest. */
  g: id,
  /** The room. */
  r: id,
  role: z.enum(["host", "guest"]),
  /** The guest's nickname, for the room's roster. */
  n: z.string().check(z.minLength(1), z.maxLength(24)),
  /** The invite they're joining through (its id: the SHA-256 of the link's secret), if any. */
  inv: z.optional(z.string().check(z.regex(/^[A-Za-z0-9_-]{1,64}$/))),
  /** The extra on the guest's bean (ADR 0018), if they picked one: an id from @obean/beans. */
  b: z.optional(z.string().check(z.regex(BEAN_EXTRA))),
  /** The paid packs the guest owns (P4-02, ADR 0013): a host's unlock its missions in the room. */
  packs: z.optional(
    z.array(z.string().check(z.regex(/^[a-z][a-z0-9-]{0,39}$/))).check(z.maxLength(16)),
  ),
  /** They have the Obean Pass (ADR 0022): a room they host shows nobody ads (ADR 0026). */
  pass: z.optional(z.literal(true)),
  /** Expiry, in seconds since the epoch. */
  exp: z.number().check(z.int(), z.positive()),
  /** The signing key's id. */
  kid: z.string().check(z.regex(/^[a-z0-9]{1,16}$/)),
});
export type RoomTokenClaims = z.infer<typeof roomTokenClaims>;

/** The room token offered with a WebSocket upgrade, if the client offered the room protocol too. */
export function tokenFromProtocols(header: string | null): string | null {
  const offered = (header ?? "").split(",").map((p) => p.trim());
  if (!offered.includes(ROOM_PROTOCOL)) return null;
  const token = offered.find((p) => p.startsWith(TOKEN_PREFIX));
  return token ? token.slice(TOKEN_PREFIX.length) : null;
}
